import { useState } from 'react';
import { m } from 'framer-motion';
import { clsx } from 'clsx';

interface ContactFormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface ContactFormProps {
  onSubmit?: (data: ContactFormData) => Promise<void>;
}

// Cloud Function endpoint for contact form submission
const CONTACT_FUNCTION_URL = import.meta.env.PROD
  ? 'https://asia-northeast3-lightivewebsite.cloudfunctions.net/submitContact'
  : 'http://127.0.0.1:5001/lightivewebsite/asia-northeast3/submitContact';

const getInputClass = () =>
  clsx(
    'w-full px-0 py-2.5',
    'font-kr text-sm',
    'bg-transparent',
    'border-0 border-b border-white/20',
    'text-white placeholder:text-white/30',
    'transition-colors duration-200',
    'focus:outline-none focus:border-b-accent',
    'hover:border-b-white/30'
  );

export function ContactForm({ onSubmit }: ContactFormProps) {
  const inputClass = getInputClass();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validate = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    }

    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다.';
    }

    if (!formData.message.trim()) {
      newErrors.message = '메시지를 입력해주세요.';
    } else if (formData.message.length > 5000) {
      newErrors.message = '메시지는 5000자 이내로 작성해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        // Submit to Cloud Function
        const response = await fetch(CONTACT_FUNCTION_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (!response.ok) {
          if (result.code === 'RATE_LIMIT_EXCEEDED') {
            setErrors({ message: '너무 많은 요청입니다. 잠시 후 다시 시도해주세요.' });
            return;
          }
          throw new Error(result.error || 'Failed to submit');
        }
      }
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      setErrors({ message: '전송에 실패했습니다. 다시 시도해주세요.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  if (isSubmitted) {
    return (
      <m.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <div className="w-16 h-16 mx-auto mb-6 border-2 border-white bg-transparent flex items-center justify-center">
          <svg
            className="w-8 h-8 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="font-en font-bold text-2xl uppercase tracking-widest text-white mb-2">
          Thank You
        </h3>
        <p className="font-kr text-white/70">
          메시지가 전송되었습니다. 빠른 시일 내에 답변드리겠습니다.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="mt-6 font-en font-bold text-[11px] uppercase tracking-widest text-white/40 hover:text-white border-b border-white/20 hover:border-white transition-all duration-200 pb-1"
        >
          새 메시지 작성
        </button>
      </m.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label
          htmlFor="name"
          className="block font-en uppercase text-xs tracking-widest text-white/40 mb-3"
        >
          이름 <span className="text-white/60">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="홍길동"
          className={clsx(inputClass, errors.name && 'border-b-red-500')}
        />
        {errors.name && (
          <p className="mt-2 text-xs font-en font-bold uppercase tracking-wider text-red-400">{errors.name}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block font-en uppercase text-xs tracking-widest text-white/40 mb-3"
        >
          이메일 <span className="text-white/60">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="hello@example.com"
          className={clsx(inputClass, errors.email && 'border-b-red-500')}
        />
        {errors.email && (
          <p className="mt-2 text-xs font-en font-bold uppercase tracking-wider text-red-400">{errors.email}</p>
        )}
      </div>

      {/* Company (Optional) */}
      <div>
        <label
          htmlFor="company"
          className="block font-en uppercase text-xs tracking-widest text-white/40 mb-3"
        >
          회사/소속 <span className="text-white/25">(선택)</span>
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="회사명"
          className={inputClass}
        />
      </div>

      {/* Message */}
      <div>
        <label
          htmlFor="message"
          className="block font-en uppercase text-xs tracking-widest text-white/40 mb-3"
        >
          메시지 <span className="text-white/60">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="프로젝트에 대해 알려주세요..."
          rows={6}
          className={clsx(
            inputClass,
            'resize-none',
            errors.message && 'border-b-red-500'
          )}
        />
        {errors.message && (
          <p className="mt-2 text-xs font-en font-bold uppercase tracking-wider text-red-400">{errors.message}</p>
        )}
        <p className="mt-1 text-xs text-white/25 text-right">
          {formData.message.length} / 5000
        </p>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={clsx(
          'w-full py-4 font-en uppercase text-sm tracking-widest',
          'bg-transparent text-white',
          'border border-[color:var(--color-rule)]',
          'transition-colors duration-200',
          'hover:border-white',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2">
            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
            SENDING...
          </span>
        ) : (
          'SEND MESSAGE'
        )}
      </button>
    </form>
  );
}
